import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';

import { acquireAccessToken } from '@/lib/auth/msal';
import {
  AZURE_API,
  AZURE_CLIENT_ID,
  AZURE_TENANT_ID,
  FRONTEND_API_URL,
  REDIRECT_URL,
} from '@/lib/env';
import { FindGraphUsersResponse } from '@/typings/api';
import { getUserProfileImage } from '@/lib/utils/user';

// Preserve OData special characters ($, (, ), ;, ,) unencoded so Graph API
// can correctly parse system query options like $expand=manager($select=id)
const paramsSerializer = (params: Record<string, unknown>) =>
  Object.entries(params)
    .map(([key, value]) => {
      const encodedValue = String(value).replace(
        /[^a-zA-Z0-9$().,:;=@_~-]/g,
        encodeURIComponent
      );
      return `${key}=${encodedValue}`;
    })
    .join('&');

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {} /* eslint-disable-line @typescript-eslint/no-empty-object-type */,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const accessToken = await acquireAccessToken(
    AZURE_CLIENT_ID,
    AZURE_TENANT_ID,
    REDIRECT_URL,
    AZURE_API
  );

  if (!accessToken) {
    throw new Error('Unauthorized');
  }

  return fetchBaseQuery({
    baseUrl: `${FRONTEND_API_URL}/v1/graph`,
    paramsSerializer,
    prepareHeaders: (headers) => {
      headers.set('authorization', `Bearer ${accessToken}`);
      return headers;
    },
  })(args, api, extraOptions);
};

export const graphApi = createApi({
  reducerPath: 'graphApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['GraphUserProfile', 'GraphUserReports'],
  endpoints: (builder) => ({
    getUserProfileImage: builder.query<string, { email: string }>({
      query: ({ email }) => ({
        url: getUserProfileImage(email).replace('/graph', ''),
        responseHandler: async (response) => {
          if (!response.ok) {
            return '';
          }

          const imageBlob = await response.blob();

          return URL.createObjectURL(imageBlob);
        },
      }),
      async onCacheEntryAdded(_args, { cacheDataLoaded, cacheEntryRemoved }) {
        try {
          const { data } = await cacheDataLoaded;

          await cacheEntryRemoved;
          URL.revokeObjectURL(data);
        } catch {
          return;
        }
      },
    }),

    findGraphUsers: builder.query<
      FindGraphUsersResponse,
      { search: string; filter?: string; orderBy?: string; select?: string }
    >({
      query: ({ search, filter, orderBy, select }) => ({
        url: '/beta/users',
        params: {
          $search: search,
          $filter: filter,
          orderBy,
          $select: select,
          $expand: 'manager($levels=1;$select=id,displayName,mail)',
        },
      }),
    }),
  }),
});
